import React, { useCallback, useEffect, useState } from "react";
import { Grid } from "@mui/material";
import {
  ArrayInput,
  AutocompleteInput,
  FormDataConsumer,
  ReferenceInput,
  required,
  SelectInput,
  SimpleFormIterator,
} from "react-admin";
import { useWatch } from "react-hook-form";
import { settings } from "app";
import { StateExamAssignmentTypeChoices } from "utils/choices";
import CustomDateTimeInput from "server/components/Fields/CustomDateTimeInput";
import RoomInput from "server/components/Fields/RoomInput";
import ProfessorInput from "server/components/Fields/ProfessorInput";
import StudyGroupField from "server/components/Fields/StudyGroupField";
import { StateExamAssignmentType } from "utils/constants";
import { graduationRequests } from "api/graduationApi";
import dataProvider from "server/dataProvider";
import IndexField from "../IndexField";
import { InternshipSessionInput } from "server/components/Fields/InternshipSessionInput";
import AcademicYearInput from "components/LearningActivity/CreateWeeklySchedule/components/Inputs/AcademicYearInput";
import { administrationRequests } from "api/administrationApi";

const CreateForm = ({ filters }) => {
  const [internshipSessionFromDate, setInternshipSessionFromDate] = useState(null);
  const [internshipSessionToDate, setInternshipSessionToDate] = useState(null);

  const formAcademicYearId = useWatch({ name: "academicYearId" });
  const formStructuralUnitId = useWatch({ name: "structuralUnitId" });
  const formInternshipSessionId = useWatch({ name: "internshipSessionId" });

  const academicYearId = formAcademicYearId ?? filters?.academicYearId;
  const structuralUnitId = formStructuralUnitId ?? filters?.structuralUnitId;
  const internshipSessionId = formInternshipSessionId ?? filters?.internshipSessionId;

  const fetchInternshipSessionsDate = useCallback(async () => {
    try {
      const { data } = await dataProvider.customRequest(
        graduationRequests.getInternshipSession(internshipSessionId),
        null,
        "GET",
      );
      setInternshipSessionFromDate(data?.startDate);
      setInternshipSessionToDate(data?.endDate);
    } catch (err) {
      console.error(err);
    }
  }, [internshipSessionId]);

  useEffect(() => {
    if (!internshipSessionId) return;
    fetchInternshipSessionsDate();
  }, [internshipSessionId, fetchInternshipSessionsDate]);

  return (
    <>
      <Grid
        container
        spacing={2}
      >
        <Grid
          item
          xs={12}
          md={4}
        >
          <AcademicYearInput withoutLanguage />
        </Grid>
        <Grid
          item
          xs={12}
          md={4}
        >
          <ReferenceInput
            source="structuralUnitId"
            reference={administrationRequests.getStructuralUnitsSelect()}
            filter={{ filterByUser: true }}
          >
            <SelectInput
              label="Факултет"
              optionText="name"
              validate={required()}
            />
          </ReferenceInput>
        </Grid>
        {structuralUnitId && academicYearId && (
          <Grid
            item
            xs={12}
            md={4}
          >
            <InternshipSessionInput
              key={`${structuralUnitId}-${academicYearId}`}
              filter={{ structuralUnitId, academicYearId }}
            />
          </Grid>
        )}
        <Grid
          item
          xs={12}
          md={4}
        >
          <ReferenceInput
            source="specialityId"
            reference={administrationRequests.getSpecialitiesSelect()}
            filter={{ filterByUser: true }}
          >
            <AutocompleteInput
              label="Специалност"
              filterToQuery={q => (q?.length > 2 ? { name: q } : {})}
            />
          </ReferenceInput>
        </Grid>
        <Grid
          item
          xs={12}
        >
          <ArrayInput
            source="exams"
            label="Изпити"
            defaultValue={[{}]}
          >
            <SimpleFormIterator
              disableReordering
              disableClear
              inline
              sx={settings.style.iteratorWithoutGap}
            >
              <FormDataConsumer>
                {({ scopedFormData }) => (
                  <Grid
                    container
                    spacing={2}
                  >
                    <Grid
                      item
                      xs={12}
                      md={4}
                    >
                      <ReferenceInput
                        source="stateExamId"
                        reference={administrationRequests.getStateExamsSelect()}
                      >
                        <SelectInput
                          optionText="name"
                          emptyText={false}
                          label="Държавен изпит"
                        />
                      </ReferenceInput>
                    </Grid>
                    <Grid
                      item
                      xs={12}
                      md={4}
                    >
                      <SelectInput
                        label="Тип назначение"
                        source="assignmentType"
                        choices={StateExamAssignmentTypeChoices}
                        optionText={rec => rec.name}
                        emptyText={false}
                      />
                    </Grid>
                    <Grid
                      item
                      xs={12}
                      md={4}
                    >
                      <CustomDateTimeInput
                        source="examTimestamp"
                        label="Дата и час на изпит"
                        minDate={internshipSessionFromDate}
                        maxDate={internshipSessionToDate}
                      />
                    </Grid>
                    <Grid
                      item
                      xs={12}
                      md={8}
                    >
                      <RoomInput />
                    </Grid>
                    <Grid
                      item
                      xs={12}
                    >
                      <ArrayInput
                        source="commissions"
                        label="Комисии"
                        defaultValue={[{ professors: [{}] }]}
                      >
                        <SimpleFormIterator
                          disableReordering
                          disableClear
                          inline={false}
                          sx={settings.style.iteratorWithoutGap}
                        >
                          <IndexField />
                          <ArrayInput
                            source="professors"
                            label=" "
                            sx={{ ml: 2 }}
                          >
                            <SimpleFormIterator
                              disableReordering
                              disableClear
                              inline
                              sx={settings.style.iteratorWithoutGap}
                            >
                              <ProfessorInput
                                source="employeeId"
                                fullWidth
                                filters={{ filterByUser: true }}
                              />
                            </SimpleFormIterator>
                          </ArrayInput>
                        </SimpleFormIterator>
                      </ArrayInput>
                    </Grid>
                    {scopedFormData?.assignmentType === StateExamAssignmentType.ForStudyGroups && (
                      <Grid
                        item
                        xs={12}
                        md={4}
                      >
                        <StudyGroupField
                          required
                          label=" Пример за въвеждане на Учебни групи: 1-5, 8, 11-13, 15"
                        />
                      </Grid>
                    )}
                  </Grid>
                )}
              </FormDataConsumer>
            </SimpleFormIterator>
          </ArrayInput>
        </Grid>
      </Grid>
    </>
  );
};

export default CreateForm;
